import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { InterfaceSummary } from '@/hooks/useDeviceTrafficSummary';
import { cn } from '@/lib/utils';
import { formatBits, formatPercent, formatTotal } from './format';
import { faults, isFrontPanelPort, portName } from './rows';

type SortKey = 'rank' | 'name' | 'bits' | 'util' | 'errors' | 'discards' | 'flaps';

/**
 * The base a row folds into, or null if it stands on its own.
 *
 * A unit whose base is not in this list has nothing to fold into; it stays a row
 * of its own rather than disappearing into a parent that is not here.
 */
function foldsInto(row: InterfaceSummary, present: Set<string>): string | null {
  return row.role === 'unit' && row.baseInterfaceId !== null && present.has(row.baseInterfaceId)
    ? row.baseInterfaceId
    : null;
}

/** The name of another row in this list, when a row wants to name it. */
function portNameOf(rows: InterfaceSummary[], interfaceId: string): string | null {
  const found = rows.find((row) => row.interfaceId === interfaceId);
  return found ? portName(found) : null;
}

interface Column {
  key: SortKey;
  label: string;
  numeric?: boolean;
  title?: string;
}

const COLUMNS: Column[] = [
  { key: 'rank', label: 'Why' },
  { key: 'name', label: 'Port' },
  { key: 'bits', label: 'Peak', numeric: true, title: 'Highest rate in either direction' },
  { key: 'util', label: 'Util', numeric: true, title: 'Peak rate as a share of line rate' },
  { key: 'errors', label: 'Errors', numeric: true },
  { key: 'discards', label: 'Discards', numeric: true },
  { key: 'flaps', label: 'Flaps', numeric: true },
];

/**
 * Every port, as a table.
 *
 * Past roughly seven meaningful classes a table is the honest form, and this is
 * simultaneously the escape hatch to the tail, the table-view twin the ranking
 * above it owes its reader, and the only affordable way to show 125 ports at
 * once.
 *
 * The default order is the server's, and the "why" chip names the column that
 * put each row where it is. Sorting is a reader's own question and re-sorting
 * replaces that order — so the chip stays, because after a sort by peak rate
 * the reason a row was originally third is no longer visible from its position.
 */
export function PortLedger({
  rows,
  cap,
  onSelect,
  compare,
}: {
  rows: InterfaceSummary[];
  /** Rows to show before the disclosure. Undefined shows all of them. */
  cap?: number;
  onSelect?: (row: InterfaceSummary) => void;
  /**
   * Promote rows into a comparison. Absent on surfaces that have nowhere to
   * put one — the node panel is height-constrained and has no chart slot.
   */
  compare?: {
    selected: string[];
    limit: number;
    onToggle: (row: InterfaceSummary) => void;
  };
}) {
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'rank', descending: false });
  const [expanded, setExpanded] = useState(false);
  // Defaults to the front panel, because that is the list an operator can
  // check against the device in front of them. juniper2 reports 125 interfaces
  // and has 52 ports; the other 73 are units of those ports, tunnels, loopbacks
  // and internal pseudo-interfaces. "All" is still one click away, and nothing
  // is removed from the data — only from the default view.
  const [filter, setFilter] = useState<'ports' | 'all' | 'faults' | 'up'>('ports');
  const [openUnits, setOpenUnits] = useState<string[]>([]);

  // A logical unit is another view of a port already in this table, not a
  // second port. Listing both is how a 51-port Junos switch read as 125 rows
  // with `ge-0/0/15` and `ge-0/0/15.0` side by side. Units fold into the base
  // they run on; one whose base is not in this list stays a row of its own
  // rather than disappearing into a parent that is not here.
  const present = useMemo(() => new Set(rows.map((row) => row.interfaceId)), [rows]);

  const unitsOf = useMemo(() => {
    const by = new Map<string, InterfaceSummary[]>();
    for (const row of rows) {
      const base = foldsInto(row, present);
      if (base === null) continue;
      by.set(base, [...(by.get(base) ?? []), row]);
    }
    return by;
  }, [rows, present]);

  const ports = useMemo(() => rows.filter((row) => foldsInto(row, present) === null), [rows, present]);

  const filtered = useMemo(
    () =>
      ports.filter((row) => {
        // Filter on the whole family. A fault on a folded unit would otherwise
        // hide its port from the faults view — the fold is a presentation, and
        // it must not swallow a finding.
        const family = [row, ...(unitsOf.get(row.interfaceId) ?? [])];
        if (filter === 'ports') return isFrontPanelPort(row);
        if (filter === 'faults') return family.some((r) => faults(r) > 0 || r.flaps > 0 || r.resetInRange);
        if (filter === 'up') return family.some((r) => r.operStatus === 'up');
        return true;
      }),
    [ports, unitsOf, filter],
  );

  const sorted = useMemo(() => {
    if (sort.key === 'rank') return filtered;
    const value = (row: InterfaceSummary): number | string => {
      switch (sort.key) {
        case 'name':
          return portName(row);
        case 'bits':
          return Math.max(row.bitsIn ?? 0, row.bitsOut ?? 0);
        case 'util':
          return row.utilization ?? -1;
        case 'errors':
          return (row.errorsIn ?? 0) + (row.errorsOut ?? 0);
        case 'discards':
          return (row.discardsIn ?? 0) + (row.discardsOut ?? 0);
        default:
          return row.flaps;
      }
    };
    return [...filtered].sort((a, b) => {
      const [x, y] = [value(a), value(b)];
      // Port names are `ge-0/0/2` and `ge-0/0/10`, so they sort naturally or
      // they sort wrong. Everything else is a number.
      const cmp = typeof x === 'string' && typeof y === 'string'
        ? x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' })
        : Number(x) - Number(y);
      return sort.descending ? -cmp : cmp;
    });
  }, [filtered, sort]);

  const visible = cap !== undefined && !expanded ? sorted.slice(0, cap) : sorted;
  const hidden = sorted.length - visible.length;

  const toggle = (key: SortKey) => {
    setSort((previous) =>
      previous.key === key ? { key, descending: !previous.descending } : { key, descending: key !== 'name' },
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h3 className="text-xs font-semibold">{filter === 'ports' ? 'Ports' : 'All interfaces'}</h3>
        <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label="Filter ports">
          {([['ports', 'Ports'], ['all', 'All'], ['faults', 'With faults'], ['up', 'Connected']] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => { setFilter(value); }}
              aria-pressed={filter === value}
              className={cn(
                'px-2.5 py-1 text-xs border-r border-border last:border-r-0',
                filter === value ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-auto rounded-md border border-border-subtle">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border-subtle">
              {compare ? <th className="w-8 px-2 py-1.5" /> : null}
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  title={column.title}
                  className={cn('px-2 py-1.5 font-medium whitespace-nowrap', column.numeric ? 'text-right' : 'text-left')}
                >
                  <button
                    type="button"
                    onClick={() => { toggle(column.key); }}
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  >
                    {column.label}
                    {sort.key === column.key ? (
                      sort.descending ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                    ) : null}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {visible.map((row) => (
              <tr key={row.interfaceId} className={cn(row.operStatus !== 'up' && 'text-muted-foreground')}>
                {compare ? (
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      className="align-middle"
                      checked={compare.selected.includes(row.interfaceId)}
                      // The limit+1-th tick is refused with a reason rather than
                      // silently ignored or quietly given a hue of its own.
                      disabled={
                        !compare.selected.includes(row.interfaceId) && compare.selected.length >= compare.limit
                      }
                      title={
                        !compare.selected.includes(row.interfaceId) && compare.selected.length >= compare.limit
                          ? `Compare up to ${String(compare.limit)} ports`
                          : `Compare ${portName(row)}`
                      }
                      onChange={() => { compare.onToggle(row); }}
                      aria-label={`Compare ${portName(row)}`}
                    />
                  </td>
                ) : null}
                <td className="px-2 py-1 whitespace-nowrap">
                  <span className="text-muted-foreground">{row.reason}</span>
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={onSelect ? () => { onSelect(row); } : undefined}
                    className={cn('text-foreground', onSelect && 'underline underline-offset-2 hover:text-foreground')}
                    disabled={!onSelect}
                  >
                    {portName(row)}
                  </button>
                  {/* `aggregated` means only "out of the ranking", which is
                      true of a LAG member and of a logical unit for different
                      reasons. Labelling from it alone called every Junos unit a
                      LAG member. The role says which, and the aggregate's
                      id lets the chip name what absorbed it. */}
                  {row.aggregateInterfaceId !== null ? (
                    <span className="ml-1.5 text-muted-foreground">
                      member of {portNameOf(rows, row.aggregateInterfaceId) ?? 'a LAG'}
                    </span>
                  ) : null}
                  {row.operStatus !== null && row.operStatus !== 'up' ? (
                    <span className="ml-1.5 text-muted-foreground">{row.operStatus}</span>
                  ) : null}
                  {(unitsOf.get(row.interfaceId) ?? []).length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="ml-1.5 text-muted-foreground underline underline-offset-2 hover:text-foreground"
                        onClick={() => {
                          setOpenUnits((open) =>
                            open.includes(row.interfaceId)
                              ? open.filter((id) => id !== row.interfaceId)
                              : [...open, row.interfaceId],
                          );
                        }}
                      >
                        {(unitsOf.get(row.interfaceId) ?? []).length === 1
                          ? '1 unit'
                          : `${String((unitsOf.get(row.interfaceId) ?? []).length)} units`}
                      </button>
                      {openUnits.includes(row.interfaceId) ? (
                        <span className="block mt-0.5 text-muted-foreground">
                          {(unitsOf.get(row.interfaceId) ?? []).map((unit) => (
                            <button
                              key={unit.interfaceId}
                              type="button"
                              onClick={onSelect ? () => { onSelect(unit); } : undefined}
                              disabled={!onSelect}
                              className={cn('block', onSelect && 'underline underline-offset-2 hover:text-foreground')}
                            >
                              {portName(unit)}{' '}
                              {formatBits(Math.max(unit.bitsIn ?? 0, unit.bitsOut ?? 0))}
                            </button>
                          ))}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </td>
                <Cell value={row.bitsIn === null && row.bitsOut === null ? null : Math.max(row.bitsIn ?? 0, row.bitsOut ?? 0)} format={formatBits} />
                <Cell value={row.utilization} format={formatPercent} />
                <Cell value={count(row.errorsIn, row.errorsOut)} format={formatTotal} reset={row.resetInRange} />
                <Cell value={count(row.discardsIn, row.discardsOut)} format={formatTotal} reset={row.resetInRange} />
                <Cell value={row.flaps} format={(v) => String(v)} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {compare && compare.selected.length >= compare.limit ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Comparing {compare.limit} ports, the most this chart can tell apart. Untick one to add another.
        </p>
      ) : null}

      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => { setExpanded(true); }}
          className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
        >
          All {sorted.length} ports →
        </button>
      ) : null}
      {sorted.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No port matches this filter.</p>
      ) : null}
    </div>
  );
}

/**
 * A missing count is an em-dash and a reset says so.
 *
 * Never a zero: a counter that restarted and a counter that stayed at zero are
 * opposite findings, and a blank cell in a table of numbers reads as the
 * quieter one.
 */
function Cell({
  value,
  format,
  reset,
}: {
  value: number | null;
  format: (value: number) => string;
  reset?: boolean;
}) {
  return (
    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
      {value === null ? (
        <span className="text-muted-foreground" title={reset === true ? 'counter reset in range' : 'not reported'}>
          {reset === true ? 'reset' : '—'}
        </span>
      ) : (
        format(value)
      )}
    </td>
  );
}

function count(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
}
